import { useLocation, useHistory } from 'react-router-dom';
import { match } from 'path-to-regexp';
import { useWhiteLabellingContext } from 'contexts/WhiteLabellingContext';
import routes, {
  AUTH_GATE_COPY_PROFILE,
  AUTH_GATE_CHOOSE_ACCOUNT,
  LP_VEHICLE,
} from 'routes/CommonRoutes';
import getUserType from 'utils/getUserType';
import { AuthGateDecision } from 'further-types/further-user';
import isInvestorOnboardingCompleteOrSkipped from 'helpers/user/isInvestorOnboardingCompleteOrSkipped';
import { useAuthClaims } from 'hooks/ui/useAuthClaims';

const adviserOnlyPaths = routes
  .filter((route) => route.isAdviserOnlyRoute)
  .map((route) => match(route.path, { decode: decodeURIComponent }));
const investorOnlyPaths = routes
  .filter((route) => route.isInvestorOnlyRoute)
  .map((route) => match(route.path, { decode: decodeURIComponent }));
const organisationOnlyPaths = routes
  .filter((route) => route.isOrganisationOnlyRoute)
  .map((route) => match(route.path, { decode: decodeURIComponent }));

export const adviserHomepageRedirect = ({
  user,
  goTo,
  fromState,
  isAdminUserImpersonatingFurtherUser,
}) => {
  if (!user?.isOnboardingComplete && !isAdminUserImpersonatingFurtherUser) {
    goTo('/adviser-onboarding?step=1');
  } else {
    if (
      fromState?.pathname &&
      !investorOnlyPaths.some((match) => match(fromState.pathname)) &&
      !organisationOnlyPaths.some((match) => match(fromState.pathname))
    ) {
      const { pathname, search = '', hash = '' } = fromState;
      goTo(pathname + search + hash);
    } else {
      goTo('/adviser-dashboard');
    }
  }
};

export const organisationHomepageRedirect = ({ goTo, fromState }) => {
  if (
    fromState?.pathname &&
    !adviserOnlyPaths.some((match) => match(fromState.pathname)) &&
    !investorOnlyPaths.some((match) => match(fromState.pathname))
  ) {
    const { pathname, search = '', hash = '' } = fromState;
    goTo(pathname + search + hash);
  } else {
    goTo('/org-dashboard');
  }
};

export const investorHomepageRedirect = ({
  user,
  goTo,
  fromState,
  isSubdomain,
}) => {
  const { userIsLpInvestor } = getUserType(user);

  if (userIsLpInvestor) {
    goTo(LP_VEHICLE);
  } else {
    if (!isInvestorOnboardingCompleteOrSkipped(isSubdomain, user)) {
      goTo('/onboarding');
    } else if (
      fromState?.pathname &&
      !adviserOnlyPaths.some((match) => match(fromState.pathname)) &&
      !organisationOnlyPaths.some((match) => match(fromState.pathname))
    ) {
      const { pathname, search = '', hash = '' } = fromState;
      goTo(pathname + search + hash);
    } else {
      goTo('/portal');
    }
  }
};

const handleAuthGateDecision = ({ user, history, fromState }) => {
  const destination =
    user?.authGateDecision === AuthGateDecision.CopyProfile
      ? AUTH_GATE_COPY_PROFILE
      : AUTH_GATE_CHOOSE_ACCOUNT;

  history.push(destination, ...(fromState ? [{ from: fromState }] : []));
};

export const useHomepageRedirect = () => {
  const { isSubdomain } = useWhiteLabellingContext();
  const history = useHistory();
  const location = useLocation();
  const { isAdminUserImpersonatingFurtherUser } = useAuthClaims();

  type PossibleState = {
    from?: {
      pathname: string;
      search: string;
      hash: string;
    };
  };
  const fromState = (location?.state as PossibleState)?.from;

  const goTo = (to: string) => {
    if (`${location?.pathname}${location?.search}${location?.hash}` !== to) {
      history.push(to);
    }
  };
  const goToClearFromState = (to: string) => {
    if (`${location?.pathname}${location?.search}${location?.hash}` !== to) {
      history.push(to, { state: {} });
    }
  };

  return function ({ user, cancelLpInvestorRedirectOnGreenSite = false }) {
    const { userIsAdviser, userIsOrg, userIsInvestor, userIsLpInvestor } =
      getUserType(user);
    const isLoggedIn = user?.isAuthenticated;

    if (isLoggedIn) {
      if (user?.authGateDecision) {
        handleAuthGateDecision({ history, user, fromState });
      } else if (userIsAdviser) {
        adviserHomepageRedirect({
          goTo: goToClearFromState,
          user,
          fromState,
          isAdminUserImpersonatingFurtherUser,
        });
      } else if (userIsOrg) {
        organisationHomepageRedirect({ goTo: goToClearFromState, fromState });
      } else if (userIsInvestor) {
        if (
          cancelLpInvestorRedirectOnGreenSite &&
          !isSubdomain &&
          userIsLpInvestor
        ) {
          return;
        } else {
          investorHomepageRedirect({
            goTo: goToClearFromState,
            user,
            fromState,
            isSubdomain,
          });
        }
      }
    } else {
      if (isSubdomain) {
        goTo(`/login${location.search}`);
      } else {
        goTo('/');
      }
    }
  };
};
